import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useDiceBox, type RollResult } from '../../hooks/useDiceBox';

export interface DiceRollResultData {
  diceValues: number[];    // All dice rolled (for display)
  keptValues: number[];    // Dice that count toward total
  droppedValues: number[]; // Dice that were dropped (kh/kl)
  diceTotal: number;       // Sum of kept dice only
  total: number;           // diceTotal + modifier
  modifier: number;
}

export interface DiceGroup {
  count: number;
  sides: number;
  values: number[];
  keep?: number;        // Number of dice to keep
  keepHighest?: boolean; // true = keep highest, false = keep lowest
}

interface DiceRollerOverlayProps {
  isOpen: boolean;
  onClose: (result?: DiceRollResultData) => void;
  diceGroups: DiceGroup[];
  modifier?: number;
  onRollComplete?: (result: DiceRollResultData) => void;
}

/**
 * Extract dice values from DiceBox results.
 * DiceBox returns results in this format:
 * { qty: 2, sides: 20, rolls: [{ value: 19, ... }, { value: 15, ... }], value: 34 }
 */
function extractDiceValues(results: RollResult[]): number[] {
  const values: number[] = [];

  for (const result of results) {
    // Primary format: rolls array with objects containing 'value'
    if (result.rolls && Array.isArray(result.rolls)) {
      for (const roll of result.rolls) {
        if (typeof roll === 'object' && roll !== null && typeof roll.value === 'number') {
          values.push(roll.value);
        } else if (typeof roll === 'number') {
          // Fallback: rolls might be plain numbers
          values.push(roll);
        }
      }
    }

    // Fallback: try sets format (older dice-box versions)
    if (values.length === 0 && result.sets && Array.isArray(result.sets)) {
      for (const set of result.sets) {
        // Check dice array
        if (set.dice && Array.isArray(set.dice)) {
          for (const die of set.dice) {
            if (typeof die.value === 'number') {
              values.push(die.value);
            }
          }
        }
        // Check nested rolls
        if (set.rolls && Array.isArray(set.rolls)) {
          for (const rollGroup of set.rolls) {
            if (rollGroup.rolls && Array.isArray(rollGroup.rolls)) {
              for (const die of rollGroup.rolls) {
                if (typeof die.value === 'number') {
                  values.push(die.value);
                }
              }
            }
          }
        }
      }
    }
  }

  return values;
}

/**
 * Build notation for DiceBox - SIMPLE notation only.
 * DiceBox doesn't understand kh/kl, so we pass "2d20" not "2d20kh1".
 * We apply kh/kl logic ourselves when processing results.
 */
function buildNotation(diceGroups: DiceGroup[]): string[] {
  return diceGroups.map(g => `${g.count}d${g.sides}`);
}

/**
 * Calculate the correct total respecting keep-highest/keep-lowest rules.
 * DiceBox returns raw values; we apply the kh/kl logic ourselves.
 */
function calculateKeptTotal(diceValues: number[], diceGroups: DiceGroup[]): {
  keptValues: number[];
  droppedValues: number[];
  total: number;
} {
  // If no keep rules, sum all values
  const hasKeepRules = diceGroups.some(g => g.keep !== undefined && g.keep > 0 && g.keep < g.count);
  if (!hasKeepRules) {
    return {
      keptValues: diceValues,
      droppedValues: [],
      total: diceValues.reduce((a, b) => a + b, 0),
    };
  }

  // Apply keep rules per dice group
  let valueIndex = 0;
  const allKept: number[] = [];
  const allDropped: number[] = [];

  for (const group of diceGroups) {
    const groupValues = diceValues.slice(valueIndex, valueIndex + group.count);
    valueIndex += group.count;

    if (group.keep !== undefined && group.keep > 0 && group.keep < group.count) {
      // Sort and partition into kept/dropped
      const sorted = [...groupValues].sort((a, b) =>
        group.keepHighest ? b - a : a - b  // Descending for kh, ascending for kl
      );
      allKept.push(...sorted.slice(0, group.keep));
      allDropped.push(...sorted.slice(group.keep));
    } else {
      allKept.push(...groupValues);
    }
  }

  return {
    keptValues: allKept,
    droppedValues: allDropped,
    total: allKept.reduce((a, b) => a + b, 0),
  };
}

/**
 * Compute the final result data from dice values and groups.
 * Shared logic used by both the useMemo and callback.
 */
function computeResultData(
  diceValues: number[],
  diceGroups: DiceGroup[],
  modifier: number
): DiceRollResultData {
  if (diceValues.length === 0) {
    // Fallback for empty values
    return {
      diceValues: [],
      keptValues: [],
      droppedValues: [],
      diceTotal: 0,
      total: modifier,
      modifier,
    };
  }

  const { keptValues, droppedValues, total: diceTotal } = calculateKeptTotal(diceValues, diceGroups);

  return {
    diceValues,
    keptValues,
    droppedValues,
    diceTotal,
    total: diceTotal + modifier,
    modifier,
  };
}

function buildDisplayNotation(diceGroups: DiceGroup[], modifier: number): string {
  const dice = diceGroups.map(g => {
    let notation = `${g.count}d${g.sides}`;
    if (g.keep !== undefined && g.keep > 0 && g.keep < g.count) {
      notation += g.keepHighest ? `kh${g.keep}` : `kl${g.keep}`;
    }
    return notation;
  }).join('+');
  if (modifier === 0) return dice;
  return modifier > 0 ? `${dice}+${modifier}` : `${dice}${modifier}`;
}

export function DiceRollerOverlay({
  isOpen,
  onClose,
  diceGroups,
  modifier = 0,
  onRollComplete,
}: DiceRollerOverlayProps) {
  // Check reduced motion preference early but don't return yet
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const notation = useMemo(() => buildNotation(diceGroups), [diceGroups]);
  const notationDisplay = useMemo(() => buildDisplayNotation(diceGroups, modifier), [diceGroups, modifier]);

  const [hasRolled, setHasRolled] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [rollResults, setRollResults] = useState<RollResult[] | null>(null);
  const hasInitiatedRoll = useRef(false);
  const finalResultRef = useRef<DiceRollResultData | null>(null);

  const computedResult = useMemo((): DiceRollResultData | null => {
    if (!rollResults || rollResults.length === 0) return null;
    const diceValues = extractDiceValues(rollResults);

    if (diceValues.length === 0) {
      // Fallback: use DiceBox's value field (note: DiceBox uses 'value' not 'total')
      const fallbackTotal = rollResults.reduce((sum, r) => sum + ((r as { value?: number }).value || r.total || 0), 0);
      return computeResultData([fallbackTotal], [{ count: 1, sides: 0, values: [fallbackTotal] }], modifier);
    }

    return computeResultData(diceValues, diceGroups, modifier);
  }, [rollResults, modifier, diceGroups]);

  const onCloseRef = useRef(onClose);
  const onRollCompleteRef = useRef(onRollComplete);

  // Update refs in effect to avoid lint warning about refs during render
  useEffect(() => {
    onCloseRef.current = onClose;
    onRollCompleteRef.current = onRollComplete;
  }, [onClose, onRollComplete]);

  const handleCloseWithAnimation = useCallback(() => {
    setIsClosing((prev) => {
      if (prev) return prev;
      setTimeout(() => {
        onCloseRef.current(finalResultRef.current ?? undefined);
      }, 500);
      return true;
    });
  }, []);

  const handleRollComplete = useCallback((results: RollResult[]) => {
    setRollResults(results);
    setTimeout(() => {
      setShowResult(true);
      const diceValues = extractDiceValues(results);

      let result: DiceRollResultData;
      if (diceValues.length === 0) {
        // Fallback: use DiceBox's value field
        const fallbackTotal = results.reduce((sum, r) => sum + ((r as { value?: number }).value || r.total || 0), 0);
        result = computeResultData([fallbackTotal], [{ count: 1, sides: 0, values: [fallbackTotal] }], modifier);
      } else {
        result = computeResultData(diceValues, diceGroups, modifier);
      }

      finalResultRef.current = result;
      onRollCompleteRef.current?.(result);
      setTimeout(() => {
        handleCloseWithAnimation();
      }, 2000);
    }, 300);
  }, [modifier, diceGroups, handleCloseWithAnimation]);

  // Only enable DiceBox if NOT reduced motion
  const { isReady, roll, clear, error } = useDiceBox({
    containerId: '#dice-canvas',
    enabled: isOpen && !prefersReducedMotion,
    onRollComplete: handleRollComplete,
  });

  useEffect(() => {
    if (isOpen && !prefersReducedMotion && isReady && notation.length > 0 && !hasInitiatedRoll.current) {
      hasInitiatedRoll.current = true;
      setHasRolled(true);
      roll(notation);
    }
  }, [isOpen, prefersReducedMotion, isReady, notation, roll]);

  useEffect(() => {
    if (!isOpen) {
      setHasRolled(false);
      setShowResult(false);
      setIsClosing(false);
      setRollResults(null);
      hasInitiatedRoll.current = false;
      finalResultRef.current = null;
      clear();
    }
  }, [isOpen, clear]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showResult && !isClosing) {
        handleCloseWithAnimation();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, showResult, isClosing, handleCloseWithAnimation]);

  const handleClick = useCallback(() => {
    if (showResult && !isClosing) {
      handleCloseWithAnimation();
    }
  }, [showResult, isClosing, handleCloseWithAnimation]);

  // Now we can do conditional returns after all hooks
  if (!isOpen) return null;

  // Reduced motion: show result immediately using pre-computed values
  if (prefersReducedMotion) {
    const diceValues = diceGroups.flatMap(g => g.values);
    const { keptValues, droppedValues, total: diceTotal } = calculateKeptTotal(diceValues, diceGroups);
    const total = diceTotal + modifier;
    const result: DiceRollResultData = { diceValues, keptValues, droppedValues, diceTotal, total, modifier };

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
        onClick={() => onClose(result)}
        role="dialog"
        aria-modal="true"
      >
        <div className="text-center">
          <div
            className="text-8xl md:text-9xl font-display text-accent-gold mb-4"
            style={{ textShadow: '0 0 40px rgba(255, 85, 0, 0.6)' }}
          >
            {total}
          </div>
          <div className="text-text-secondary text-xl font-display">{notationDisplay}</div>
          <div className="text-text-muted text-lg mt-3 font-mono">
            ({keptValues.join(' + ')})
            {droppedValues.length > 0 && (
              <span className="text-text-muted/50 line-through ml-2">
                {droppedValues.join(', ')}
              </span>
            )}
            {modifier !== 0 && ` ${modifier > 0 ? '+' : ''}${modifier}`}
            {' = '}{total}
          </div>
          <div className="text-text-muted text-sm mt-4">Click to close</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90"
      onClick={handleClick}
      role="dialog"
      aria-modal="true"
    >
      {error && (
        <div className="absolute top-4 left-4 right-4 bg-error/20 border border-error rounded-lg p-4 text-error z-10">
          <p className="font-bold">Failed to load 3D dice</p>
          <p className="text-sm mt-1">{error.message}</p>
          <button className="mt-2 px-4 py-2 bg-error text-white rounded" onClick={() => onClose()}>
            Close
          </button>
        </div>
      )}

      <div
        id="dice-canvas"
        ref={(el) => {
          if (el) {
            el.style.width = `${window.innerWidth}px`;
            el.style.height = `${window.innerHeight}px`;
          }
        }}
        style={{ position: 'absolute', top: 0, left: 0 }}
      />

      {!isReady && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-text-secondary animate-pulse">Loading dice...</div>
        </div>
      )}

      {isReady && hasRolled && !showResult && (
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <div className="text-text-secondary text-lg animate-pulse">Rolling {notationDisplay}...</div>
        </div>
      )}

      {showResult && computedResult && (
        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-all duration-500 ${isClosing ? 'opacity-0' : ''}`}>
          <div className={`text-center ${isClosing ? 'animate-dice-result-shrink' : 'animate-dice-result-appear'}`}>
            <div
              className={`text-8xl md:text-9xl font-display text-accent-gold mb-4 ${isClosing ? '' : 'animate-dice-result-pulse'}`}
              style={{ textShadow: '0 0 40px rgba(255, 85, 0, 0.6)' }}
            >
              {computedResult.total}
            </div>
            <div className={`text-text-secondary text-xl font-display transition-opacity duration-200 ${isClosing ? 'opacity-0' : ''}`}>
              {notationDisplay}
            </div>
            {computedResult.keptValues.length > 0 && (
              <div className={`text-text-muted text-lg mt-3 font-mono transition-opacity duration-200 ${isClosing ? 'opacity-0' : ''}`}>
                ({computedResult.keptValues.join(' + ')})
                {computedResult.droppedValues.length > 0 && (
                  <span className="text-text-muted/50 line-through ml-2">
                    {computedResult.droppedValues.join(', ')}
                  </span>
                )}
                {modifier !== 0 && ` ${modifier > 0 ? '+' : ''}${modifier}`}
                {' = '}{computedResult.total}
              </div>
            )}
          </div>
        </div>
      )}

      <div role="status" aria-live="polite" className="sr-only">
        {showResult && computedResult ? `Roll complete: ${computedResult.total}` : `Rolling ${notationDisplay}`}
      </div>
    </div>
  );
}
